import { useRef } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { LuLightbulb } from "react-icons/lu";

import type {
  DirectoryInventoryListItem,
  RealAssetResponse,
} from "@/apiClient";
import useBrowseInfiniteScroll from "@/hooks/useBrowseInfiniteScroll";
import { formatIsoDateStr } from "@/utils";

import BufferedSearchInput from "./BufferedSearchInput";
import TemplatePathPills from "./TemplatePathPills";
import type { DocumentedDirectoryRow } from "./folderConfig.utils";

export type FolderConfigDirectoryTab = "needs-docs" | "documented";

type FolderConfigDirectoryListPanelProps = {
  tab: FolderConfigDirectoryTab;
  suggestedQuery: string;
  documentedQuery: string;
  documentedPatternCount: number;
  awaitingDocumentationCount: number;
  totalSuggestedDirectoryMatches: number;
  documentedDirectoryRows: DocumentedDirectoryRow[];
  filteredSuggestedDirectories: DirectoryInventoryListItem[];
  filteredDocumentedDirectoryRows: DocumentedDirectoryRow[];
  hasMoreSuggestedDirectories: boolean;
  isLoadingMoreSuggestedDirectories: boolean;
  templatePath: string;
  editingDirectoryPublicId: string;
  realAssetsById: Map<string, RealAssetResponse>;
  onTabChange: (tab: FolderConfigDirectoryTab) => void;
  onSuggestedQueryChange: (value: string) => void;
  onDocumentedQueryChange: (value: string) => void;
  onLoadMoreSuggestedDirectories: () => void;
  onUseSuggestion: (item: DirectoryInventoryListItem) => void;
  onEditDocumentedDirectory: (row: DocumentedDirectoryRow) => void;
  onStartNewPattern: () => void;
};

function SuggestedDirectoryCard({
  item,
  isSelected,
  realAssetsById,
  onUseSuggestion,
}: {
  item: DirectoryInventoryListItem;
  isSelected: boolean;
  realAssetsById: Map<string, RealAssetResponse>;
  onUseSuggestion: (item: DirectoryInventoryListItem) => void;
}) {
  return (
    <button
      type="button"
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        isSelected
          ? "border-blue-100 bg-blue-50/60"
          : "border-slate-200 bg-slate-50 hover:border-blue-50"
      }`}
      onClick={() => onUseSuggestion(item)}
    >
      <TemplatePathPills
        templatePath={item.canonical_path || ""}
        realAssetsById={realAssetsById}
      />
      <p className="mt-2 text-xs font-normal text-grey-200">
        <span className="font-medium text-grey-300">
          {(item.concrete_directory_count ?? 0).toLocaleString()}
        </span>{" "}
        directories across{" "}
        <span className="font-medium text-grey-300">
          {(item.cruise_count ?? 0).toLocaleString()}
        </span>{" "}
        cruises
      </p>
    </button>
  );
}

function DocumentedDirectoryCard({
  row,
  isSelected,
  realAssetsById,
  onEditDocumentedDirectory,
}: {
  row: DocumentedDirectoryRow;
  isSelected: boolean;
  realAssetsById: Map<string, RealAssetResponse>;
  onEditDocumentedDirectory: (row: DocumentedDirectoryRow) => void;
}) {
  const curatorLabel =
    row.curatedBy?.email || row.definition.curated_by_user_id || "Unknown";
  const dataExpertLabel =
    row.dataExpert?.email || row.definition.data_expert_user_id || "";

  return (
    <button
      type="button"
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        isSelected
          ? "border-blue-100 bg-blue-50/60"
          : "border-slate-200 bg-slate-50 hover:border-blue-50"
      }`}
      onClick={() => onEditDocumentedDirectory(row)}
    >
      <TemplatePathPills
        templatePath={row.definition.template_path}
        realAssetsById={realAssetsById}
      />
      <div className="mt-2 space-y-1">
        <p className="text-xs font-normal text-grey-200">
          {row.inventory ? (
            <>
              <span className="font-medium text-grey-300">
                {(row.inventory.concrete_directory_count ?? 0).toLocaleString()}
              </span>{" "}
              directories across{" "}
              <span className="font-medium text-grey-300">
                {(row.inventory.cruise_count ?? 0).toLocaleString()}
              </span>{" "}
              cruises
            </>
          ) : (
            "No matching directories yet"
          )}
        </p>
        {dataExpertLabel ? (
          <p className="text-xs font-normal text-grey-200">
            Data expert · {dataExpertLabel}
          </p>
        ) : null}
        <div className="flex items-center gap-1.5 text-xs font-normal text-grey-200">
          {row.definition.allow_addendum_upload ? (
            <FiCheck
              className="shrink-0 text-emerald-600"
              aria-hidden="true"
              size={12}
            />
          ) : (
            <FiX
              className="shrink-0 text-rose-500"
              aria-hidden="true"
              size={12}
            />
          )}
          <p>
            Addendum Uploads{" "}
            {row.definition.allow_addendum_upload ? "Approved" : "Disallowed"}
          </p>
        </div>
      </div>
      <p className="mt-1 text-xs text-grey-100">
        Documented by {curatorLabel}
        {row.definition.curated_dt
          ? ` · ${formatIsoDateStr(row.definition.curated_dt)}`
          : ""}
      </p>
    </button>
  );
}

function SectionHelpText({ tab }: { tab: FolderConfigDirectoryTab }) {
  return (
    <div className="shrink-0 px-4 py-3">
      <div className="grid [&>div]:col-start-1 [&>div]:row-start-1">
        <div
          className={`flex items-start gap-[10px] text-[13px] leading-[1.45] text-slate-500 transition-opacity ${
            tab === "needs-docs"
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
        >
          <LuLightbulb
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-amber-500"
            size={20}
          />
          <p>
            These directories haven&apos;t been documented yet. Pick one to get
            started — you&apos;ll describe its folder pattern, expected
            contents, and known variations. This builds a shared reference for
            our cruise archives and helps QA spot pattern divergences.
          </p>
        </div>
        <div
          className={`flex items-start gap-[10px] text-[13px] leading-[1.45] text-slate-500 transition-opacity ${
            tab === "documented"
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
        >
          <LuLightbulb
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-amber-500"
            size={20}
          />
          <p>
            These directories have already been documented. Click any entry to
            review or edit its docs. This shared reference helps QA efforts and
            keeps cruise archive patterns consistent.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FolderConfigDirectoryListPanel({
  tab,
  suggestedQuery,
  documentedQuery,
  documentedPatternCount,
  awaitingDocumentationCount,
  totalSuggestedDirectoryMatches,
  documentedDirectoryRows,
  filteredSuggestedDirectories,
  filteredDocumentedDirectoryRows,
  hasMoreSuggestedDirectories,
  isLoadingMoreSuggestedDirectories,
  templatePath,
  editingDirectoryPublicId,
  realAssetsById,
  onTabChange,
  onSuggestedQueryChange,
  onDocumentedQueryChange,
  onLoadMoreSuggestedDirectories,
  onUseSuggestion,
  onEditDocumentedDirectory,
  onStartNewPattern,
}: FolderConfigDirectoryListPanelProps) {
  const suggestedListRef = useRef<HTMLDivElement | null>(null);
  const suggestedSentinelRef = useRef<HTMLDivElement | null>(null);

  useBrowseInfiniteScroll({
    enabled: tab === "needs-docs",
    hasMore: hasMoreSuggestedDirectories,
    isBusy: isLoadingMoreSuggestedDirectories,
    rootMarginPx: 180,
    scrollContainerRef: suggestedListRef,
    sentinelRef: suggestedSentinelRef,
    onIntersect: onLoadMoreSuggestedDirectories,
  });

  return (
    <div className="relative min-h-[26rem] xl:min-h-0">
      <div className="absolute inset-0 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex shrink-0 border-b border-slate-200">
          <button
            type="button"
            className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
              tab === "needs-docs"
                ? "border-b-2 border-blue-200 text-blue-300"
                : "text-grey-200 hover:text-blue-200"
            }`}
            onClick={() => onTabChange("needs-docs")}
          >
            Needs Docs ({awaitingDocumentationCount})
          </button>
          <button
            type="button"
            className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
              tab === "documented"
                ? "border-b-2 border-blue-200 text-blue-300"
                : "text-grey-200 hover:text-blue-200"
            }`}
            onClick={() => onTabChange("documented")}
          >
            Documented ({documentedPatternCount})
          </button>
        </div>

        <SectionHelpText tab={tab} />

        <div className="shrink-0 border-b border-slate-100 p-3">
          {tab === "needs-docs" ? (
            <BufferedSearchInput
              value={suggestedQuery}
              onCommit={onSuggestedQueryChange}
              className="w-full rounded-md border border-slate-400 bg-white py-[7px] pl-10 pr-10 text-sm text-blue-300 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none"
              placeholder="Filter by path..."
              inputId="curated-directory-suggested-search"
              inputName="suggested_search"
            />
          ) : (
            <BufferedSearchInput
              value={documentedQuery}
              onCommit={onDocumentedQueryChange}
              className="w-full rounded-md border border-slate-400 bg-white py-[7px] pl-10 pr-10 text-sm text-blue-300 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none"
              placeholder="Search by path, description, or person..."
              inputId="curated-directory-documented-search"
              inputName="documented_search"
            />
          )}
        </div>

        <div
          ref={suggestedListRef}
          className="min-h-0 flex-1 space-y-1.5 overflow-auto p-3"
        >
          {tab === "needs-docs" ? (
            filteredSuggestedDirectories.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-grey-200">
                {totalSuggestedDirectoryMatches === 0 && !suggestedQuery.trim()
                  ? "Everything has been documented!"
                  : "No matches for that filter."}
              </p>
            ) : (
              <>
                {filteredSuggestedDirectories.map((item) => (
                  <SuggestedDirectoryCard
                    key={item.canonical_path}
                    item={item}
                    isSelected={
                      !editingDirectoryPublicId &&
                      templatePath === item.canonical_path
                    }
                    realAssetsById={realAssetsById}
                    onUseSuggestion={onUseSuggestion}
                  />
                ))}
                <div
                  ref={suggestedSentinelRef}
                  aria-hidden="true"
                  className="h-1 w-full"
                />
                {isLoadingMoreSuggestedDirectories ? (
                  <p className="px-3 py-2 text-center text-xs text-grey-200">
                    Loading more directories...
                  </p>
                ) : null}
              </>
            )
          ) : filteredDocumentedDirectoryRows.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-grey-200">
              {documentedDirectoryRows.length === 0
                ? "Nothing documented yet."
                : "No results for that search."}
            </p>
          ) : (
            filteredDocumentedDirectoryRows.map((row) => (
              <DocumentedDirectoryCard
                key={row.definition.public_id || row.definition.template_path}
                row={row}
                isSelected={
                  editingDirectoryPublicId === row.definition.public_id
                }
                realAssetsById={realAssetsById}
                onEditDocumentedDirectory={onEditDocumentedDirectory}
              />
            ))
          )}
        </div>

        <div className="shrink-0 border-t border-slate-200 p-3">
          <button
            type="button"
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-grey-200 transition-colors hover:border-blue-100 hover:text-blue-200"
            onClick={onStartNewPattern}
          >
            + New Pattern
          </button>
        </div>
      </div>
    </div>
  );
}
